'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import AppLogo from '@/components/ui/AppLogo';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageCircle,
  Building2,
  Send,
  CheckCircle,
  ChevronRight,
  User,
  Briefcase,
  Users,
  ArrowRight,
  Navigation,
  Star,
} from 'lucide-react';

const officialBranch = {
  name: 'FIFTY ONE PLUS LAB — Sector 51, Noida',
  address: 'H-56, Sector 51, Noida, Uttar Pradesh – 201301',
  phone: '+91 98189 86535',
  phone2: '+91 78358 19665',
  email: 'info@51pluslab.com',
  hours: 'Mon–Sun: 7:00 AM – 8:00 PM (All Days)',
  landmark: 'Near Sector 51, ~3 km from Sector 52 Metro Station',
  mapEmbed:
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3505.2!2d77.3686560!3d28.5786861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390ce5dd283991b7%3A0x84cb44448754ca35!2sFIFTY%20ONE%20PLUS%20LAB%2C%20NOIDA%2C%20ONE%20PLUS%20LAB%20(Powered%20by%20BeLife)!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin',
  directionsUrl:
    'https://www.google.com/maps/place/FIFTY+ONE+PLUS+LAB,+NOIDA,+ONE+PLUS+LAB+(Powered+by+BeLife)/@28.5786861,77.368656,17z',
  badge: 'Official Branch',
  badgeColor: 'bg-blue-600 text-white',
};

const inquiryTypes = [
  { value: 'general', label: 'General Inquiry', icon: User },
  { value: 'corporate', label: 'Corporate Health Checkup', icon: Building2 },
  { value: 'home_collection', label: 'Home Sample Collection', icon: MapPin },
  { value: 'packages', label: 'Health Packages', icon: Briefcase },
  { value: 'bulk_booking', label: 'Bulk / Group Booking', icon: Users },
];

interface FormData {
  name: string;
  phone: string;
  email: string;
  company: string;
  employees: string;
  inquiryType: string;
  message: string;
}

export default function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    company: '',
    employees: '',
    inquiryType: 'general',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const isCorporate = formData.inquiryType === 'corporate' || formData.inquiryType === 'bulk_booking';

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Hero Banner */}
      <section className="relative overflow-hidden hero-gradient text-white">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(14,165,233,0.2) 0%, transparent 40%)',
            }}
          />
        </div>
        <div className="absolute right-8 top-1/2 -translate-y-1/2 opacity-5 hidden lg:block">
          <AppLogo size={220} />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-16 lg:py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-sm font-medium mb-5">
              <MapPin size={14} />
              H-56, Sector 51, Noida · Powered by BeLife
            </div>
            <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight mb-4">
              Contact <span className="text-sky-300">51 Plus Lab</span>
            </h1>
            <p className="text-lg text-blue-100 mb-8 leading-relaxed">
              Reach us for appointments, corporate health checkup proposals, home collection bookings, or any diagnostic inquiry. Our team responds within 30 minutes during working hours.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://wa.me/919818986535?text=Hello%2051%20Plus%20Lab%2C%20I%20need%20assistance"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <MessageCircle size={18} />
                WhatsApp Us
              </a>
              <a
                href="tel:+919818986535"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <Phone size={18} />
                +91 98189 86535
              </a>
              <a
                href={officialBranch.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              >
                <Navigation size={18} />
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick CTA Strip */}
      <section className="bg-white border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
            <a
              href="https://wa.me/919818986535?text=Hello%2051%20Plus%20Lab%2C%20I%20need%20assistance"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 px-6 py-5 hover:bg-green-50 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                <MessageCircle size={22} className="text-green-600" />
              </div>
              <div>
                <div className="font-bold text-foreground text-sm">WhatsApp</div>
                <div className="text-muted-foreground text-xs">+91 98189 86535 · Instant reply</div>
              </div>
              <ChevronRight size={16} className="ml-auto text-muted-foreground group-hover:text-green-600 transition-colors" />
            </a>
            <a
              href="tel:+919818986535"
              className="flex items-center gap-4 px-6 py-5 hover:bg-blue-50 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                <Phone size={22} className="text-blue-600" />
              </div>
              <div>
                <div className="font-bold text-foreground text-sm">Call Us</div>
                <div className="text-muted-foreground text-xs">+91 98189 86535 · 7 AM – 8 PM</div>
              </div>
              <ChevronRight size={16} className="ml-auto text-muted-foreground group-hover:text-blue-600 transition-colors" />
            </a>
            <a
              href="mailto:info@51pluslab.com"
              className="flex items-center gap-4 px-6 py-5 hover:bg-sky-50 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0 group-hover:bg-sky-200 transition-colors">
                <Mail size={22} className="text-sky-600" />
              </div>
              <div>
                <div className="font-bold text-foreground text-sm">Email</div>
                <div className="text-muted-foreground text-xs">info@51pluslab.com · Reply in 2 hrs</div>
              </div>
              <ChevronRight size={16} className="ml-auto text-muted-foreground group-hover:text-sky-600 transition-colors" />
            </a>
            <a
              href={officialBranch.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 px-6 py-5 hover:bg-orange-50 transition-colors group"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <Navigation size={22} className="text-orange-600" />
              </div>
              <div>
                <div className="font-bold text-foreground text-sm">Get Directions</div>
                <div className="text-muted-foreground text-xs">H-56, Sector 51, Noida</div>
              </div>
              <ChevronRight size={16} className="ml-auto text-muted-foreground group-hover:text-orange-600 transition-colors" />
            </a>
          </div>
        </div>
      </section>

      {/* Google Rating Banner */}
      <section className="bg-blue-50 border-b border-blue-100 py-4">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
          <div className="flex flex-wrap items-center justify-center gap-6 text-center">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={18} className={s <= 4 ? 'text-yellow-400 fill-yellow-400' : 'text-yellow-400 fill-yellow-200'} />
                ))}
              </div>
              <div>
                <span className="font-extrabold text-xl text-foreground">4.7</span>
                <span className="text-muted-foreground text-sm ml-1">/ 5</span>
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">663+ Reviews</span> on Google
              </div>
            </div>
            <div className="h-6 w-px bg-border hidden md:block" />
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle size={16} className="text-green-600" />
              NABL Accredited
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle size={16} className="text-green-600" />
              CGHS Empanelled
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle size={16} className="text-green-600" />
              Powered by BeLife
            </div>
            <a
              href={officialBranch.directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold text-primary hover:underline"
            >
              View on Google Maps →
            </a>
          </div>
        </div>
      </section>

      {/* Branch Details + Map */}
      <section className="py-16 lg:py-20 bg-background">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-extrabold text-foreground mb-3">
              Our Official Branch
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Visit our NABL-accredited diagnostic centre at Sector 51, Noida for walk-in tests, consultations, or sample collection.
            </p>
          </div>

          {/* Single Branch Card + Map */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
            {/* Branch Info */}
            <div className="lg:col-span-2 p-8 flex flex-col gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center flex-shrink-0">
                  <MapPin size={18} className="text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-extrabold text-xl text-foreground">{officialBranch.name}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${officialBranch.badgeColor}`}>
                      {officialBranch.badge}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{officialBranch.address}</p>
                  <p className="text-primary/70 text-xs mt-1 font-medium">{officialBranch.landmark}</p>
                </div>
              </div>

              <div className="space-y-3">
                <a
                  href={`tel:${officialBranch.phone.replace(/\s/g, '')}`}
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Phone size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Phone (Click to Call)</div>
                    <div className="font-bold text-foreground text-sm">{officialBranch.phone}</div>
                    <div className="font-semibold text-foreground text-xs">{officialBranch.phone2}</div>
                  </div>
                </a>

                <a
                  href="https://wa.me/919818986535?text=Hello%2051%20Plus%20Lab%2C%20I%20need%20assistance"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3.5 rounded-xl bg-green-50 hover:bg-green-100 transition-colors group"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-100 group-hover:bg-green-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <MessageCircle size={16} className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">WhatsApp</div>
                    <div className="font-bold text-foreground text-sm">Chat with us instantly</div>
                  </div>
                </a>

                <div className="flex items-center gap-3 p-3.5 rounded-xl bg-amber-50">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Clock size={16} className="text-amber-600" />
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground font-medium">Working Hours</div>
                    <div className="font-semibold text-foreground text-xs leading-relaxed">{officialBranch.hours}</div>
                  </div>
                </div>
              </div>

              <a
                href={officialBranch.directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-white font-semibold text-sm px-5 py-3 rounded-xl hover:bg-primary/90 transition-colors mt-auto justify-center"
              >
                <Navigation size={15} />
                Get Directions on Google Maps
                <ArrowRight size={14} />
              </a>
            </div>

            {/* Embedded Map */}
            <div className="lg:col-span-3 min-h-[380px] lg:min-h-0">
              <iframe
                src={officialBranch.mapEmbed}
                width="100%"
                height="100%"
                style={{ minHeight: '380px', border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="FIFTY ONE PLUS LAB — H-56, Sector 51, Noida"
                className="w-full h-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Lead Capture Form + Corporate Routing */}
      <section className="py-16 lg:py-20 bg-muted/40">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left: Corporate Info Panel */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              <div>
                <div className="inline-flex items-center gap-2 bg-secondary text-primary rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
                  <Building2 size={14} />
                  Corporate Inquiries
                </div>
                <h2 className="text-3xl font-extrabold text-foreground mb-3 leading-tight">
                  Partner with 51 Plus Lab for Corporate Health
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  We serve 50+ corporate clients across Noida with customised annual health checkup programs, pre-employment medicals, and on-site health camps.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { icon: Users, title: 'Annual Health Checkups', desc: 'Comprehensive AHC packages for 10 to 10,000+ employees' },
                  { icon: Briefcase, title: 'Pre-Employment Medical', desc: 'Fast-track medicals with digital reports within 24 hours' },
                  { icon: Building2, title: 'On-Site Medical Camps', desc: 'We bring the lab to your office — no disruption to work' },
                  { icon: CheckCircle, title: 'Insurance Medicals', desc: 'CGHS empanelled — serving central government employees & pensioners' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 p-4 bg-white rounded-xl border border-border">
                    <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                      <item.icon size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-bold text-foreground text-sm">{item.title}</div>
                      <div className="text-muted-foreground text-xs mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-br from-primary to-sky-500 rounded-2xl p-6 text-white">
                <div className="flex items-center gap-3 mb-3">
                  <AppLogo size={36} />
                  <div>
                    <div className="font-extrabold text-base">51 Plus Lab</div>
                    <div className="text-blue-100 text-xs">NABL Accredited · CGHS Empanelled · Powered by BeLife</div>
                  </div>
                </div>
                <p className="text-blue-100 text-sm leading-relaxed mb-4">
                  Get a customised corporate health checkup proposal within 24 hours. Fill the form and our corporate team will call you back.
                </p>
                <a
                  href="https://wa.me/919818986535?text=Hello%2C%20I%20need%20a%20corporate%20health%20checkup%20proposal%20for%20my%20company"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors"
                >
                  <MessageCircle size={16} />
                  WhatsApp for Corporate Proposal
                </a>
              </div>
            </div>

            {/* Right: Lead Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-border shadow-sm p-8">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                      <CheckCircle size={32} className="text-green-600" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-foreground">Thank You!</h3>
                    <p className="text-muted-foreground max-w-sm">
                      Your inquiry has been received. Our team will contact you within 30 minutes during working hours.
                    </p>
                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => setSubmitted(false)}
                        className="px-5 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors"
                      >
                        Submit Another
                      </button>
                      <Link
                        href="/book-appointment"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
                      >
                        Book Appointment
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="mb-6">
                      <h3 className="text-2xl font-extrabold text-foreground mb-1">Send Us a Message</h3>
                      <p className="text-muted-foreground text-sm">
                        Fill in your details and we&apos;ll get back to you promptly.
                      </p>
                    </div>

                    <div className="mb-5">
                      <label className="block text-sm font-semibold text-foreground mb-2">
                        Inquiry Type <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {inquiryTypes.map((type) => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, inquiryType: type.value }))}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-xs font-semibold transition-all ${
                              formData.inquiryType === type.value
                                ? 'bg-primary text-white border-primary' :'bg-white text-foreground border-border hover:border-primary hover:text-primary'
                            }`}
                          >
                            <type.icon size={13} />
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-1.5">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Your full name"
                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-foreground mb-1.5">
                            Phone Number <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="+91 XXXXX XXXXX"
                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Email Address
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                        />
                      </div>

                      {isCorporate && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                          <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5">
                              Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              required={isCorporate}
                              placeholder="Your company name"
                              className="w-full px-4 py-2.5 rounded-xl border border-border bg-white text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-foreground mb-1.5">
                              No. of Employees
                            </label>
                            <select
                              name="employees"
                              value={formData.employees}
                              onChange={handleChange}
                              className="w-full px-4 py-2.5 rounded-xl border border-border bg-white text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                            >
                              <option value="">Select range</option>
                              <option value="10-50">10 – 50</option>
                              <option value="51-200">51 – 200</option>
                              <option value="201-500">201 – 500</option>
                              <option value="501-1000">501 – 1,000</option>
                              <option value="1000+">1,000+</option>
                            </select>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-semibold text-foreground mb-1.5">
                          Message
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          placeholder="Describe your requirement, test name, or any specific question..."
                          className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-70 text-white font-bold py-3.5 rounded-xl transition-colors text-sm"
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send size={16} />
                            {isCorporate ? 'Request Corporate Proposal' : 'Send Inquiry'}
                          </>
                        )}
                      </button>

                      <p className="text-center text-xs text-muted-foreground">
                        Or reach us instantly via{' '}
                        <a
                          href="https://wa.me/919818986535"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-600 font-semibold hover:underline"
                        >
                          WhatsApp
                        </a>
                        {' '}·{' '}
                        <a href="tel:+919818986535" className="text-primary font-semibold hover:underline">
                          +91 98189 86535
                        </a>
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
